import { z } from "zod";

import { queryClient } from "@/api/core";
import { type Status } from "@/schemas";
import { isAdPlaceholderId } from "@/utils/ads";

import type { EntitySchema } from "@/api/core/types";
import type { InfiniteData, QueryKey } from "@tanstack/react-query";

export interface PaginatedResult<T> {
  result: T[];
  hasMore: boolean;
  link?: string;
}

interface Entity {
  id: string;
}

const isEntity = <T = unknown>(object: T): object is T & Entity => {
  return (
    typeof object === "object" && object !== null && "id" in (object as object)
  );
};

const hasPages = (data: unknown): data is { pages: unknown[] } =>
  typeof data === "object" && data !== null && "pages" in data;

/** Deduplicate an array of entities by their ID. */
const deduplicateById = <T extends Entity>(entities: T[]): T[] => {
  const map = entities.reduce<Map<string, T>>((result, entity) => {
    return result.set(entity.id, entity);
  }, new Map());

  return Array.from(map.values());
};

/** Flatten paginated results into a single array. */
const flattenPages = <T>(
  queryData: InfiniteData<PaginatedResult<T>> | T[] | T | undefined,
): T[] | undefined => {
  let data: T[] = [];

  if (!queryData) {
    return data;
  }

  if (Array.isArray(queryData)) {
    data = queryData;
  } else if (typeof queryData === "object" && "pages" in queryData) {
    // Infinite Data
    data =
      queryData?.pages.reduce<T[]>(
        (prev: T[], curr) => [...prev, ...curr.result],
        [],
      ) || [];
  } else {
    // Single object
    data = [queryData];
  }

  if (data && data.every(isEntity)) {
    return deduplicateById(data);
  } else if (data) {
    return data;
  }
};

/** Traverse pages and update the item inside if found. */
const updatePageItem = <T>(
  queryKey: QueryKey,
  newItem: T,
  isItem: (item: T, newItem: T) => boolean,
) => {
  queryClient.setQueryData<InfiniteData<PaginatedResult<T>>>(
    queryKey,
    (data) => {
      if (data) {
        const pages = data.pages.map((page) => {
          const result = page.result.map((item) =>
            isItem(item, newItem) ? newItem : item,
          );
          return { ...page, result };
        });
        return { ...data, pages };
      }
    },
  );
};

/** Insert the new item at the end of the first page. */
const appendPageItem = <T>(queryKey: QueryKey, newItem: T) => {
  queryClient.setQueryData<InfiniteData<PaginatedResult<T>>>(
    queryKey,
    (data) => {
      if (data) {
        const pages = [...data.pages];
        pages[0] = { ...pages[0], result: [...pages[0].result, newItem] };
        return { ...data, pages };
      }
    },
  );
};

/** Insert the new item at the beggining of the first page. */
const prependPageItem = <T>(queryKey: QueryKey, newItem: T) => {
  queryClient.setQueryData<InfiniteData<PaginatedResult<T>>>(
    queryKey,
    (data) => {
      if (data) {
        const pages = [...data.pages];
        pages[0] = { ...pages[0], result: [newItem, ...pages[0].result] };
        return { ...data, pages };
      }
    },
  );
};

/** Remove an item inside if found. */
const removePageItem = <T>(
  queryKey: QueryKey,
  itemToRemove: T,
  isItem: (item: T, newItem: T) => boolean,
) => {
  queryClient.setQueriesData<InfiniteData<PaginatedResult<T>>>(
    { queryKey },
    (data) => {
      if (data) {
        const pages = data.pages.map((page) => {
          const result = page.result.filter(
            (item) => !isItem(item, itemToRemove),
          );
          return { ...page, result };
        });
        return { ...data, pages };
      }
    },
  );
};

/** Remove statuses from timeline by account ID. */
const removeAccountStatusesFromTimeline = (
  queryKey: QueryKey,
  accountId: string,
) => {
  queryClient.setQueriesData<InfiniteData<PaginatedResult<Status>>>(
    { queryKey },
    (data) => {
      if (data) {
        const pages = data.pages.map((page) => {
          const result = page.result
            .filter((item) => !isAdPlaceholderId(item.id))
            .filter((item) => item.account.id !== accountId);
          return { ...page, result };
        });
        return { ...data, pages };
      }
    },
  );
};

const paginateQueryData = <T>(array: T[] | undefined) => {
  return array?.reduce((resultArray: any, item: any, index: any) => {
    const chunkIndex = Math.floor(index / 20);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new chunk
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
};

const sortQueryData = <T>(
  queryKey: QueryKey,
  comparator: (a: T, b: T) => number,
) => {
  queryClient.setQueryData<InfiniteData<PaginatedResult<T>>>(
    queryKey,
    (prevResult) => {
      if (prevResult) {
        const nextResult = { ...prevResult };
        const flattenedQueryData = flattenPages(nextResult);
        const sortedQueryData = flattenedQueryData?.sort(comparator);
        const paginatedPages = paginateQueryData(sortedQueryData);
        const newPages = paginatedPages.map((page: T, idx: number) => ({
          ...prevResult.pages[idx],
          result: page,
        }));

        nextResult.pages = newPages;
        return nextResult;
      }
    },
  );
};

const findEntityById = (queryKey: QueryKey, id: string) => {
  const data = queryClient
    .getQueriesData<InfiniteData<PaginatedResult<any>>>({
      queryKey,
      exact: true,
    })
    .map((query) => {
      const queriesData = query[1];
      if (!queriesData) {
        return;
      }
      return flattenPages(queriesData);
    })
    .filter(Boolean);

  return data.flat().find((entity) => entity.id === id);
};

const updateQueryData = <T extends Entity>(
  queryKey: QueryKey,
  newEntity: T,
  schema?: EntitySchema<Entity>,
) => {
  queryClient
    .getQueriesData<unknown>({ queryKey })
    .forEach(([cachedKey, cachedData]) => {
      const schemaToUse = (schema ??
        z.custom<Entity>()) as EntitySchema<Entity>;
      const parsedEntity = schemaToUse.safeParse(newEntity);

      if (!parsedEntity.success) {
        return;
      }

      const parsedNextEntity = parsedEntity.data;

      if (hasPages(cachedData)) {
        // infinite pages
        updatePageItem<Entity>(
          cachedKey,
          parsedNextEntity,
          (o, n) => String(o.id) === String(n.id),
        );
      } else if (Array.isArray(cachedData)) {
        // basic arrays
        queryClient.setQueryData<Entity[]>(
          cachedKey,
          (cachedData as Entity[]).map((entity) => {
            if (entity.id === newEntity.id) {
              return parsedNextEntity;
            }

            return entity;
          }),
        );
      } else if (isEntity(cachedData) && cachedData.id === newEntity.id) {
        // single entities
        queryClient.setQueryData(cachedKey, parsedNextEntity);
      }
    });
};

const removeQueryData = (queryKey: QueryKey, id: string) => {
  queryClient
    .getQueriesData<unknown>({ queryKey })
    .forEach(([cachedKey, cachedData]) => {
      if (hasPages(cachedData)) {
        // infinite pages
        removePageItem<Entity>(
          cachedKey,
          { id },
          (o, n) => String(o.id) === String(n.id),
        );
      } else if (Array.isArray(cachedData)) {
        // basic arrays
        queryClient.setQueryData<Entity[]>(
          cachedKey,
          (cachedData as Entity[]).filter((entity) => entity.id !== id),
        );
      } else if (isEntity(cachedData) && cachedData.id === id) {
        // single entities
        queryClient.removeQueries({ queryKey: cachedKey, exact: true });
        queryClient.cancelQueries({ queryKey: cachedKey, exact: true });
      }
    });
};

export {
  flattenPages,
  updatePageItem,
  prependPageItem,
  appendPageItem,
  removePageItem,
  removeAccountStatusesFromTimeline,
  sortQueryData,
  findEntityById,
  updateQueryData,
  removeQueryData,
  hasPages,
  isEntity,
};
